// usePanZoom.js — pan/zoom for a boxed surface: wheel zooms toward the cursor,
// drag pans, and a press that does not travel is a CLICK for whatever it
// landed on. Both the client report's plan and its photo viewer run on this.
//
// 2026-09-14. Brett: "i cant close a photo unless i am fully zoomed out." The
// photo viewer captured the pointer on pointerdown while zoomed, so the ×, the
// ‹ › and Reset buttons — children of the pan surface — never received their
// click (the browser fires click on the capturing element instead). The plan
// viewer next to it already claimed the pointer only after a real drag; that
// rule now lives here once, with the maths in utils/panZoom.js.
//
// Use:
//   const boxRef = useRef(null);
//   const pz = usePanZoom(boxRef, { wheel: true, maxScale: 6 });
//   <div ref={boxRef} {...pz.handlers} style={{ overflow: 'hidden', ...pz.surfaceStyle }}>
//     <div style={{ transform: pz.transform, transformOrigin: '0 0', transition: pz.dragging ? 'none' : 'transform 0.2s ease' }}>…</div>
//   </div>
//   pz.reset() · pz.toggle(2.4) · pz.centreOn(2.6, fx, fy) · pz.isZoomed · pz.view
//
// 2026-09-15 — { pinch: true }: two fingers zoom about their midpoint and pan as
// they move (Brett, on a pin's photos: "so i can zoom in on photos"). A surface
// that pinches owns every touch on it (touch-action: none), so it is for a
// viewer that fills its box, not a surface a page scrolls past.

import { useCallback, useEffect, useRef, useState } from 'react';
import { FIT, WHEEL_STEP, clampView, zoomAt, viewCentredOn, isDragStart } from '../utils/panZoom';

export function usePanZoom(boxRef, { wheel = true, maxScale = 6, pinch = false } = {}) {
  const [view, setViewRaw] = useState(FIT);
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef(null);
  const movedRef = useRef(false);
  const pointersRef = useRef(new Map());   // pointerId → { x, y } while down (pinch)
  const pinchRef = useRef(null);            // { dist, mid, claimed } while two fingers are down
  const isZoomed = view.scale > 1.01;

  const clamp = useCallback((v) => {
    const box = boxRef.current;
    return box ? clampView(v, box.clientWidth, box.clientHeight) : v;
  }, [boxRef]);

  const reset = useCallback(() => setViewRaw(FIT), []);
  const centreOn = useCallback((S, fx = 0.5, fy = 0.5) => {
    const box = boxRef.current;
    if (!box) return;
    setViewRaw(viewCentredOn(box.clientWidth, box.clientHeight, S, fx, fy));
  }, [boxRef]);
  const toggle = useCallback((S = 2.4) => {
    setViewRaw(prev => {
      if (prev.scale > 1.01) return FIT;
      const box = boxRef.current;
      return box ? viewCentredOn(box.clientWidth, box.clientHeight, S) : prev;
    });
  }, [boxRef]);

  // Wheel zoom toward the cursor. Native non-passive listener so the page
  // does not scroll while the surface zooms.
  useEffect(() => {
    if (!wheel) return undefined;
    const box = boxRef.current;
    if (!box) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      const rect = box.getBoundingClientRect();
      const px = e.clientX - rect.left, py = e.clientY - rect.top;
      const factor = e.deltaY < 0 ? WHEEL_STEP : 1 / WHEEL_STEP;
      setViewRaw(v => zoomAt(v, px, py, factor, box.clientWidth, box.clientHeight, maxScale));
    };
    box.addEventListener('wheel', onWheel, { passive: false });
    return () => box.removeEventListener('wheel', onWheel);
  }, [boxRef, wheel, maxScale]);

  // Both fingers of a pinch are claimed once the pinch MOVES, never on the press.
  const claimPointers = (el, ids) => {
    for (const id of ids) { try { el.setPointerCapture(id); } catch (_) { /* not a pointer-capture host */ } }
  };
  const pairNow = () => {
    const pts = [...pointersRef.current.values()];
    if (pts.length < 2) return null;
    const [a, b] = pts;
    return { dist: Math.hypot(b.x - a.x, b.y - a.y), mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } };
  };

  // A press is remembered; the pointer is CLAIMED only once it has travelled.
  // Capturing on pointerdown is what stole the click from a pin, and then from
  // the photo viewer's close button.
  const onPointerDown = (e) => {
    movedRef.current = false;
    if (pinch) {
      pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointersRef.current.size === 2) { dragRef.current = null; pinchRef.current = pairNow(); return; }
    }
    if (!isZoomed) return;
    dragRef.current = { x: e.clientX, y: e.clientY, tx: view.tx, ty: view.ty, id: e.pointerId, captured: false };
  };
  const onPointerMove = (e) => {
    if (pinch && pointersRef.current.has(e.pointerId)) pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pr = pinchRef.current;
    if (pr && !dragRef.current) {
      const now = pairNow();
      const box = boxRef.current;
      if (!now || !box || !(pr.dist > 0)) return;
      if (!pr.claimed) { movedRef.current = true; setDragging(true); claimPointers(e.currentTarget, [...pointersRef.current.keys()]); }
      const rect = box.getBoundingClientRect();
      const factor = now.dist / pr.dist;
      setViewRaw(v => {
        const z = zoomAt(v, now.mid.x - rect.left, now.mid.y - rect.top, factor, box.clientWidth, box.clientHeight, maxScale);
        return clamp({ scale: z.scale, tx: z.tx + (now.mid.x - pr.mid.x), ty: z.ty + (now.mid.y - pr.mid.y) });
      });
      pinchRef.current = { ...now, claimed: true };
      return;
    }
    const dr = dragRef.current;
    if (!dr) return;
    const dx = e.clientX - dr.x, dy = e.clientY - dr.y;
    if (!dr.captured && isDragStart(dx, dy)) {
      dr.captured = true;
      movedRef.current = true;
      setDragging(true);
      try { e.currentTarget.setPointerCapture(dr.id); } catch (_) { /* not a pointer-capture host */ }
    }
    if (dr.captured) setViewRaw(v => clamp({ scale: v.scale, tx: dr.tx + dx, ty: dr.ty + dy }));
  };
  const onPointerUp = (e) => {
    if (pinch) {
      pointersRef.current.delete(e.pointerId);
      try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) { /* not captured */ }
      if (pointersRef.current.size < 2 && pinchRef.current) { pinchRef.current = null; setDragging(false); }
    }
    const dr = dragRef.current;
    if (dr && dr.captured) { try { e.currentTarget.releasePointerCapture(dr.id); } catch (_) { /* already released */ } }
    dragRef.current = null;
    setDragging(false);
  };
  // The click that ends a drag is swallowed, so panning off a pin or a button
  // does not also press it.
  const onClickCapture = (e) => {
    if (movedRef.current) { e.preventDefault(); e.stopPropagation(); movedRef.current = false; }
  };

  return {
    view, isZoomed, dragging, reset, toggle, centreOn,
    transform: `translate(${view.tx}px, ${view.ty}px) scale(${view.scale})`,
    handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp, onClickCapture },
    surfaceStyle: { touchAction: (pinch || isZoomed) ? 'none' : 'auto', cursor: isZoomed ? (dragging ? 'grabbing' : 'grab') : undefined },
  };
}
